<template>
  <div class="banner grid" :class="[colorClass, textColor]">
    <div class="top">
      <slot name="image">
        <img v-lazy="config.baseUrl + '/storage/' + image" :alt="headline" class="object-cover w-full h-full" />
      </slot>
    </div>
    <div class="bottom mt-10">
      <slot name="content">
        <div v-if="$slots.headline || $slots.icon" class="flex space-x-4 whitespace-normal items-center">
          <slot name="headline">
            <BaseHeadline v-if="overline" class="overline" :level="2" :size="Sizes.Small">{{ overline }}</BaseHeadline>
            <BaseHeadline v-if="headline" class="headline" :level="1" :size="Sizes.Small">{{ headline }}</BaseHeadline>
          </slot>
          <div v-if="$slots.icon" class="icon">
            <slot name="icon"></slot>
          </div>
        </div>
        <div v-if="$slots.text" class="mt-4">
          <slot name="text"> </slot>
        </div>
        <BaseText v-else-if="text" class="mt-4">{{ text }}</BaseText>
      </slot>
      <div v-if="$slots.cta" class="mt-10">
        <slot name="cta"></slot>
      </div>
      <BaseButton v-else class="inline-flex mt-10 md:mt-20" :class="buttonBorderColor">Test</BaseButton>
    </div>
  </div>
</template>
<script lang="ts">
import { Colors } from '@/types/colors'
import { defineComponent, computed, PropType, inject } from 'vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseHeadline from '@/components/base/BaseHeadline.vue'
import BaseText from '@/components/base/BaseText.vue'
import { Sizes } from '@/types/sizes'

export enum Orientation {
  Left = 'LEFT',
  Right = 'RIGHT',
}

export default defineComponent({
  components: {
    BaseButton,
    BaseHeadline,
    BaseText,
  },
  /**
   * @property {colors} backgoundColor
   * @property {orientation} orientation
   * @property {String} image
   * @property {String} headline
   * @property {String} overline
   * @property {String} link
   * @property {String} text
   * define properties with defaults
   */
  props: {
    backgoundColor: {
      type: String as PropType<Colors>,
    },
    orientation: {
      type: String as PropType<Orientation>,
      default: Orientation.Left,
    },
    image: {
      type: String,
      default: '',
    },
    headline: {
      type: String,
      default: '',
    },
    overline: {
      type: String,
      default: '',
    },
    link: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
    to: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    /**
     * @returns css class for orentation/direction
     */
    const orientationClass = computed(() => {
      return props.orientation === Orientation.Right ? 'md:col-start-2 md:text-right md:items-end' : 'md:items-start'
    })

    /**
     * @returns css class for background color
     */
    const colorClass = computed(() => {
      return `bg-${props.backgoundColor}`
    })

    /**
     * @returns {string} css class for text color
     */
    const textColor = computed(() => {
      if (props.backgoundColor === Colors.Black) return 'text-white'
      return 'text-black'
    })

    /**
     * @returns {string} css class for button border color
     */
    const buttonBorderColor = computed(() => {
      if (props.backgoundColor === Colors.Black) return 'border-white'
      return 'border-black'
    })

    // import config
    const config: any = inject('config')
    return { colorClass, orientationClass, scrollTo, textColor, buttonBorderColor, config, Sizes }
  },
})
</script>
<style scoped>
.overline + .headline {
  @apply mt-4;
}
</style>
